import { generateUrl, slugify } from "@repo/fika-shared/src/lib/slug.js"
import type { Locale } from "@repo/fika-shared/src/locale"
/**
 * Removes a trailing slash from a URL string, handling both bare paths
 * (https://example.com/) and paths before query strings (https://example.com/?q=1).
 * This is needed because `new URL().toString()` always adds a trailing slash to root URLs.
 */
export function removeTrailingSlash(url: string): string {
  return url.replace(/\/(\?|$)/, "$1")
}

export function publicationHost({
  publicUrl,
  userName,
}: {
  publicUrl: string
  userName: string
}) {
  const baseUrl = new URL(publicUrl)
  return `${userName.toLowerCase()}.${baseUrl.host}`
}

export function blogUrl({
  publicUrl,
  userName,
  publicationLocale,
  locale,
  searchParams,
}: {
  publicUrl: string
  userName: string
  publicationLocale: string
  locale: string
  searchParams?: URLSearchParams
}) {
  const baseUrl = new URL(publicUrl)
  const host = publicationHost({ publicUrl, userName })
  const url = `${baseUrl.protocol}//${host}`

  const isMainLocale = publicationLocale ? locale === publicationLocale : true
  const base = isMainLocale ? url : `${url}/${locale}`

  const qs = searchParams?.toString()
  return qs ? `${base}?${qs}` : base
}

export function aboutUrl({
  publicUrl,
  authorName,
  userName,
  mainLocale,
  locale,
}: {
  publicUrl: string
  authorName: string
  userName: string
  mainLocale: string
  locale: string
}) {
  const baseUrl = blogUrl({
    publicUrl,
    userName,
    publicationLocale: mainLocale,
    locale,
  })
  return `${baseUrl}/about/${slugify(authorName)}`
}

export function rssBlogUrl(
  publicUrl: string,
  userName: string,
  mainLocale: string,
) {
  return `${blogUrl({ publicUrl, userName, publicationLocale: mainLocale, locale: mainLocale })}/rss.xml`
}

export function articleUrl({
  publicUrl,
  userName,
  postMainLocale,
  locale,
  slugAndId,
  searchParams,
}: {
  publicUrl: string
  userName: string
  postMainLocale: string
  locale: string
  slugAndId: string
  searchParams?: URLSearchParams
}): string {
  const baseUrl = new URL(publicUrl)
  const host = publicationHost({ publicUrl, userName })
  const base = `${baseUrl.protocol}//${host}`
  const isMainLocale = locale === postMainLocale
  const path = isMainLocale ? slugAndId : `${locale}/${slugAndId}`
  const url = new URL(`${base}/${path}`)
  if (searchParams) {
    searchParams.forEach((value, key) => {
      url.searchParams.set(key, value)
    })
  }
  return url.toString()
}

export function extractUserNameFromBlogUrl(
  url: string,
  publicUrl: string,
): string | null {
  try {
    const parsedUrl = new URL(url)
    const expectedBase = new URL(publicUrl)

    // Check if it's a subdomain of the expected base
    const hostname = parsedUrl.hostname
    const baseHostname = expectedBase.hostname

    if (!hostname.endsWith(`.${baseHostname}`)) {
      return null
    }

    // Extract subdomain (userName)
    const userName = hostname.slice(0, -(baseHostname.length + 1))

    // Validate it's an RSS feed path
    if (parsedUrl.pathname !== "/rss.xml") {
      return null
    }

    return userName || null
  } catch {
    return null
  }
}

type ArticleUrlParams = {
  publicUrl: string
  userName: string
  postMainLocale: Locale
  locale: Locale
  title: string
  postId: string
  searchParams?: URLSearchParams
}

export function buildArticleUrl(params: ArticleUrlParams): string {
  const slugAndId = generateUrl(params.title, params.postId)
  return articleUrl({
    publicUrl: params.publicUrl,
    userName: params.userName,
    postMainLocale: params.postMainLocale,
    locale: params.locale,
    slugAndId,
    searchParams: params.searchParams,
  })
}

// TODO: unify url builders
export function buildPreviewUrl(params: {
  publicUrl: string
  userName: string
  postMainLocale: Locale
  locale: Locale
  title: string
  postId: string
  token: string
}): string {
  const searchParams = new URLSearchParams()
  searchParams.set("preview", params.token)
  return buildArticleUrl({ ...params, searchParams })
}
